import React, { useMemo } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, SparkLineProps, Usages } from "./SparkLineTypes";

export default function SparkLine(props: SparkLineProps) {
  const valueScaling = props.valueScaling || 1;
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime: props.referenceTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset
    },
  });
  
  const transformedData: { dataArray: number[], lastValueTime: null | Date } = useMemo(() => {
    if (!data?.[0]?.data?.length) return { dataArray: [], lastValueTime: null };

    const sortedData = data[0].data.sort((a, b) => a.key.localeCompare(b.key));
    const values = sortedData.map(d => d.value * valueScaling);
    const lastValueTime = new Date(sortedData[sortedData.length - 1].key);

    return { dataArray: values, lastValueTime };
  }, [data, valueScaling]);

  if (isLoading) {
    return <ProgressSpinner />;
  }

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline
    },
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
  }

  return data?.length ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={props.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      unit={props.unit}
      dataset={dataset}
      title={data[0].title}
      height={props.height}
      overlayLabel={props.overlayLabel}
      vertical={props.vertical}
      horizontal={props.horizontal}
      advanced={props.advanced}
      advancedProps={advancedProps}
    />
  ) : (
    <div style={{ height: `${props.height ?? "auto"}px` }}>{T("word.no-records")}</div>
  );
}
